import {
	classifyReconciliationTerminalEvidence,
	RESUME_OUTCOME,
	type ReconciliationEvidence,
	type ReconciliationTerminalPolicy,
	type NeedsAttentionOutcome,
	type ResumeOutcome
} from "@uploads";
import type { FileUploadSession } from "@/db/fileUploadDb";
import { deleteFileUploadSession, tryDeleteCorruptedSession } from "@/db/fileUploadDb";
import { adminUploadTelemetry } from "@/services/uploads/adminUploadTelemetry";
import { createAdminSessionStore } from "@/services/uploads/adminSessionStore";
import api from "@/api";

function logTerminalVerdict(event: string, context: Record<string, unknown>, error?: unknown): void {
	try {
		if (error === undefined) adminUploadTelemetry.logWarn(event, context);
		else adminUploadTelemetry.logWarn(event, context, error);
	} catch {
		// A telemetry outage must not change the recovery verdict or block local cleanup.
	}
}

function createAdminTerminalRecoveryPolicy(options: {
	// Returns whether the local cleanup CAS actually applied. A stale snapshot (collected before a
	// concurrent reclaim — e.g. resetUploadingSessions — changed the persisted row's owner identity)
	// must not silently report a terminal verdict: the row this decision was computed against no
	// longer exists as described, so a HANDED_OFF/LOST verdict here would race the row that reclaim
	// just made "pending" again for the drain to re-observe.
	deleteLocalSession(session: FileUploadSession, evidence: ReconciliationEvidence): Promise<boolean>;
	markRecoveryBlocked(session: FileUploadSession, reason: string): Promise<NeedsAttentionOutcome | "already-needs-attention" | void>;
	/**
	 * Review C2. Send the chunked-object `/complete` for a row whose `.part` objects have all landed.
	 * NOTHING on the server assembles those parts on its own — `assembleObjectFromParts` is reachable
	 * only as a dependency of `/complete`, and the sweep only deletes — so a landed listing is not a
	 * finished upload and the local bytes may not be dropped on the strength of it. Only a completion
	 * the server accepted (or one it answers "already sealed") licenses the hand-off.
	 *
	 * Optional so a policy built without it declines rather than deletes.
	 */
	completeChunkedObject?(session: FileUploadSession): Promise<"completed" | "refused">;
}): ReconciliationTerminalPolicy<FileUploadSession, ResumeOutcome> {
	return {
		async decide({ session, evidence }) {
			if (evidence.kind === "terminal-data") {
				logTerminalVerdict("recording.fileUpload.terminalLost", {
					sessionId: session.id,
					uploadId: session.uploadId,
					s3Key: session.s3Key,
					evidenceKind: evidence.kind,
					terminalChunkCount: evidence.chunkIndexes.length
				});
				return RESUME_OUTCOME.LOST;
			}
			const disposition = classifyReconciliationTerminalEvidence(evidence, session.totalChunks, {
				transport: session.transport
			});
			if (disposition === "remote-parts-landed") {
				// The parts are all up, but nothing server-side will ever turn them into the object. Send
				// the completion ourselves; on ANY refusal fall back to the existing settling/retry
				// machinery with the local bytes untouched.
				const completion = options.completeChunkedObject ? await options.completeChunkedObject(session) : "refused";
				if (completion !== "completed") {
					// Silent-failure rule: a declined hand-off used to be a silent local delete; this is the
					// one line that says why the row is going round again instead.
					console.warn("[Upload] Chunked-object parts have all landed but the completion was refused; keeping the local copy", {
						sessionId: session.id,
						uploadId: session.uploadId,
						registryId: session.registryId,
						attemptId: session.attemptId,
						hasCompletionPort: Boolean(options.completeChunkedObject),
						listPartsKind: evidence.listParts.kind
					});
					logTerminalVerdict("recording.fileUpload.terminalChunkedObjectCompletionRefused", {
						sessionId: session.id,
						uploadId: session.uploadId,
						s3Key: session.s3Key,
						evidenceKind: evidence.kind,
						listPartsKind: evidence.listParts.kind
					});
					return RESUME_OUTCOME.RETRY;
				}
				logTerminalVerdict("recording.fileUpload.terminalChunkedObjectCompleted", {
					sessionId: session.id,
					uploadId: session.uploadId,
					s3Key: session.s3Key,
					evidenceKind: evidence.kind,
					listPartsKind: evidence.listParts.kind
				});
				// The client completed it, so the object is ASSEMBLED — nothing is finishing in the
				// background, which is exactly the `assembled` case's COMPLETED copy (LSP-19428).
				const cleanedUpAfterCompletion = await options.deleteLocalSession(session, evidence);
				if (!cleanedUpAfterCompletion) {
					logTerminalVerdict("recording.fileUpload.terminalDecisionStale", {
						sessionId: session.id,
						uploadId: session.uploadId,
						s3Key: session.s3Key,
						evidenceKind: evidence.kind,
						computedOutcome: RESUME_OUTCOME.COMPLETED
					});
					return RESUME_OUTCOME.RETRY;
				}
				return RESUME_OUTCOME.COMPLETED;
			}
			if ((disposition === "recovery-blocked" || disposition === "remote-gone") && evidence.kind === "local-gaps") {
				const missingBufferCount = evidence.missingBufferIndexes.length;
				const survivingBufferCount = Math.max(0, session.totalChunks - missingBufferCount - evidence.missingMetadataCount);
				const markOutcome = await options.markRecoveryBlocked(
					session,
					`Local upload data is incomplete; retained ${survivingBufferCount}/${session.totalChunks} surviving chunk buffers for recovery.`
				);
				const context = {
					sessionId: session.id,
					uploadId: session.uploadId,
					s3Key: session.s3Key,
					evidenceKind: evidence.kind,
					listPartsKind: evidence.listParts.kind,
					...(evidence.listParts.kind === "verified" ? { listPartsCount: evidence.listParts.parts.length } : {}),
					totalChunks: session.totalChunks,
					survivingBufferCount,
					missingBufferCount,
					missingMetadataCount: evidence.missingMetadataCount
				};
				// LSP-19428 deliberately left this alone. It reads as "finishing in the background" for a
				// row that is durably complete, which is the same wrong copy this ticket fixes for the
				// assembled route — but it is NOT the same bug. This branch returns BEFORE
				// `deleteLocalSession`, so the durable row survives, and BOTH available verdicts lie
				// about it: HANDED_OFF violates the "a handed-off row's durable row is guaranteed gone"
				// invariant FileUploadDialog's tray reconciliation is built on, and COMPLETED violates
				// the same invariant under a different name. Moving it sideways is not a fix, and the
				// honest fix (retire the row here, or teach the tray a third state) is a terminal-write
				// change this ticket has no acceptance criteria for — AC3 calls this path "unchanged".
				if (markOutcome === "already-complete") return RESUME_OUTCOME.HANDED_OFF;
				if (markOutcome === "already-bytes-gone") return RESUME_OUTCOME.LOST;
				if (markOutcome === "already-needs-attention") return RESUME_OUTCOME.NEEDS_ATTENTION;
				if (markOutcome !== undefined) return RESUME_OUTCOME.RETRY;
				logTerminalVerdict("recording.fileUpload.terminalRecoveryBlocked", context);
				return RESUME_OUTCOME.RECOVERY_BLOCKED;
			}
			if (disposition === "remote-gone" || disposition === "read-deferred") {
				// ListParts returns the same "gone" evidence after a successful sweep completion and
				// after an abort. Without an assembled-object probe this is not a destructive verdict.
				logTerminalVerdict("recording.fileUpload.terminalDeferred", {
					sessionId: session.id,
					uploadId: session.uploadId,
					s3Key: session.s3Key,
					evidenceKind: evidence.kind,
					listPartsKind: evidence.listParts.kind
				});
				return RESUME_OUTCOME.RETRY;
			}
			// LSP-19428: `remote-complete` is TWO different server answers and only one of them leaves
			// background work behind. Branch on the evidence, never on the disposition:
			//
			//   { kind: "assembled" }                    the object is already assembled server-side.
			//                                            Nothing is "finishing" — this is COMPLETED, and
			//                                            the tray must say "Uploaded N items".
			//   { kind: "verified" }, contiguous 1..N    every part is up but the MPU is still open; the
			//                                            server sweep has to complete it. HANDED_OFF,
			//                                            "N finishing in the background" — unchanged.
			//
			// `classifyReconciliationTerminalEvidence` deliberately flattens both to "remote-complete"
			// because the DISPOSITION (is the file safe? may we drop local state?) is genuinely the same
			// for both. Only the user-facing "is anything still happening?" question differs, so the
			// split belongs here, in the app's outcome mapping, and not in the shared classifier.
			// Reading `disposition === "remote-complete"` here would fix the assembled copy by breaking
			// the verified copy, so the two cases are asserted separately in
			// adminTerminalRecoveryPolicy.assembledVsVerified.spec.ts.
			const remoteAssembled = evidence.listParts.kind === "assembled";
			const outcome =
				disposition !== "remote-complete"
					? RESUME_OUTCOME.LOST
					: remoteAssembled
						? RESUME_OUTCOME.COMPLETED
						: RESUME_OUTCOME.HANDED_OFF;
			logTerminalVerdict(
				outcome === RESUME_OUTCOME.COMPLETED
					? "recording.fileUpload.terminalRemoteAssembled"
					: outcome === RESUME_OUTCOME.HANDED_OFF
						? "recording.fileUpload.terminalHandedOff"
						: "recording.fileUpload.terminalLost",
				{
					sessionId: session.id,
					uploadId: session.uploadId,
					s3Key: session.s3Key,
					evidenceKind: evidence.kind,
					listPartsKind: evidence.listParts.kind,
					...(evidence.listParts.kind === "verified" ? { listPartsCount: evidence.listParts.parts.length } : {}),
					...(evidence.kind === "local-gaps"
						? { missingBufferCount: evidence.missingBufferIndexes.length, missingMetadataCount: evidence.missingMetadataCount }
						: {})
				},
				evidence.kind === "read-failure" ? evidence.error : undefined
			);
			const cleanedUp = await options.deleteLocalSession(session, evidence);
			if (!cleanedUp) {
				logTerminalVerdict("recording.fileUpload.terminalDecisionStale", {
					sessionId: session.id,
					uploadId: session.uploadId,
					s3Key: session.s3Key,
					evidenceKind: evidence.kind,
					computedOutcome: outcome
				});
				return RESUME_OUTCOME.RETRY;
			}
			return outcome;
		}
	};
}

export function createPersistedAdminTerminalRecoveryPolicy(): ReconciliationTerminalPolicy<FileUploadSession, ResumeOutcome> {
	const store = createAdminSessionStore();
	return createAdminTerminalRecoveryPolicy({
		markRecoveryBlocked: (session, reason) => store.markRecoveryBlocked(session, reason),
		async completeChunkedObject(session) {
			// Attempt fence only: the server re-derives the manifest from the landed `.part` objects and
			// refuses a body carrying `parts` at all (contact-api `UploadsController.complete`).
			if (!session.registryId || !session.attemptId) {
				console.warn("[Upload] Cannot complete a chunked-object upload without its attempt fence", {
					sessionId: session.id,
					uploadId: session.uploadId,
					registryId: session.registryId,
					attemptId: session.attemptId,
					generation: session.generation
				});
				return "refused";
			}
			try {
				await api.completeUploadsUpload(session.registryId, session.attemptId, session.generation ?? 1);
				return "completed";
			} catch (error) {
				console.warn("[Upload] Chunked-object recovery completion failed; the local copy is kept", {
					sessionId: session.id,
					uploadId: session.uploadId,
					registryId: session.registryId,
					attemptId: session.attemptId,
					generation: session.generation,
					message: error instanceof Error ? error.message : String(error)
				});
				return "refused";
			}
		},
		async deleteLocalSession(session, evidence) {
			if (evidence.kind === "read-failure") {
				return tryDeleteCorruptedSession(session.id, session.uploadId, session.uploadOwnerId, session.uploadOwnerClaimId, {
					requireExactOwnerIdentity: true,
					notifyOtherTabs: false
				});
			}
			return deleteFileUploadSession(session.id, session.uploadId, session.uploadOwnerId, session.uploadOwnerClaimId, {
				requireExactOwnerIdentity: true,
				notifyOtherTabs: false
			});
		}
	});
}
